/**
 * ## TODOs
 *
 * ----
 *
 * This is an index-like list of TODOs.
 *
 * Individual tasks in the code are written as "TODO ({TASK})",
 * for example:
 *
 *    TODO (DECOUPLING)
 *
 * When solving one of this tasks, remove all the related TODOs.
 * The notes are not exhaustive and you'll probably end up looking
 * in more places than the indicated ones.
 *
 * ----
 *
 * - (CHORE): Remove dead code and apply the GBM Standards.
 *
 * - (DECOUPLING): We're aiming to separate the search-box from the AppState
 *    to make it reusable and reduce side-effects, moving interactions
 *    to @Input()/@Output() properties.
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { CAPITAL_OPERATIONS } from '@models/puppet.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import {
  AppState,
  CURRENT_SYMBOL,
  NO_SCROLL,
  OPERATION_HARD_RESET,
  OPERATION_PRICE,
  SOLACE_SUBSCRIPTIONS,
} from 'src/app';
import { BootstrapService, SearchService } from 'src/core';
import {
  getMarketName,
  MarketItem,
  MarketType,
  SharedSolaceMainService,
  SharedSolaceStreamService,
} from 'src/solace';
import { SearchDataSettings } from './models/search-data-settings';

@Component({
  selector: 'hb-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInputElement: ElementRef;

  /**
   * TODO (CHORE):
   * Some properties, like items, are not required and can be
   * gotten rid of. Check tradeCapitals or tradeFunds to see how this
   * component gets rendered.
   *
   * Neither displayMemberSelected nor minSearchLength are used in this file
   * or the template. Maybe we can delete them.
   */
  @Input() placeholder: string;
  @Input() isUsaContract: boolean;

  // TODO (CHORE):
  // items is never passed as input, maybe this shouldn't be @Input
  @Input() items: SearchBoxIssueItem[];
  @Input() disabled: boolean;
  @Input() displayMemberSelected: string;
  @Input()
  public set selectedRow(rowToSelect: SearchBoxIssueItem) {
    if (rowToSelect) {
      if (this.currentRow !== rowToSelect) {
        this.currentRow = rowToSelect;
        this.filterText = rowToSelect.issueID;
        this.currentIssue = rowToSelect;
        this.showItems = false;
        this.doUpdatePrice = true;
        this.updateSearchResults();

        this.subscribeSolaceTopics(rowToSelect);
      }

      if (this.useEventEmitterInsteadOfStore) {
        this.selectedRowChange.next(rowToSelect);
        this.obtainSymbolFromIssue(rowToSelect);
      } else {
        this.store.dispatch({
          type: CURRENT_SYMBOL,
          payload: rowToSelect,
        });
      }

      this.focusOnInput();
    }
  }
  @Input()
  public propertyForDisplayedPrice: CAPITAL_OPERATIONS;

  @Input() public minSearchLength: number;
  @Input() public dataSource: SearchDataSettings[];

  /**
   * When true, it'll emit issue selection events via selectedRowChange,
   * instead of using the App State.
   *
   * This should be used when you want to decouple the search box from
   * other logic in the app.
   *
   * // TODO (DECOUPLING): Make this the default behavior and refactor the Operation
   * Puppet to handle the App State without relying on this component.
   */
  @Input() public useEventEmitterInsteadOfStore: boolean;
  @Input() public updatesFromSymbolSelector = true;

  @Output() public selectedRowChange: EventEmitter<any> = new EventEmitter();

  @Output() public clearSelectedRow: EventEmitter<any> = new EventEmitter();
  @Output() public onChange: EventEmitter<any> = new EventEmitter();

  /***/
  bootstrapPromise: Promise<any>;
  stateCtrl: FormControl;
  priceDetail: any = {};
  showlatest = true;
  showResults = false;
  attachedClick = true;
  selectedFilter = 0;
  filter: any = {
    issue: '',
  };
  doUpdatePrice = false;

  /**
   * Displays the last price and the price change in the right side of the box
   */
  public showExtraData = false;

  public currentIssue: SearchBoxIssueItem;

  /***/
  protected currentSymbolSubscription: Subscription;
  protected searchSubscription: Subscription;
  /**
   * TODO (CHORE): symbolSubscription is unsubscribed when it exists, but
   * it's never initialized or assigned.
   */
  protected symbolSubscription: Subscription = Subscription.EMPTY;
  protected currentDataSource: any[];
  public filterText: string = '';
  public itemsToShow: SearchBoxIssueItem[];
  protected currentRow: SearchBoxIssueItem;
  protected itemIndexFocused = -1;
  public showItems: boolean;
  public showNoResult: boolean;
  protected focusableElements: any;
  protected firstTabStop: any;
  protected lastTabStop: any;
  private currentMarketName: string;

  /**
   * This value gets used to create and delete solace topic subscriptions
   * Exposing it as input allows us to keep multiple subscriptions open
   * if this component gets displayed in different scenarios
   */
  @Input() private consumerId = 'search';

  constructor(
    protected _elementRef: ElementRef,
    private bootstrapService: BootstrapService,
    private searchService: SearchService,
    private store: Store<AppState>,
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private sharedSolaceMainService: SharedSolaceMainService,
  ) {}

  ngOnInit() {
    this.store.dispatch({
      type: OPERATION_HARD_RESET,
      payload: { clearText: true },
    });

    this.bootstrapPromise = this.bootstrapService.initSession().then(() => {
      this.items = this.searchService.issues;

      // Issues inside the searchService are updated in other parts of the code,
      // for now prefer mutation (inside for each) instead of using map
      this.items.forEach(
        (item: SearchBoxIssueItem & { symbol: string; symbolName: string }) => {
          if (
            item.symbol !== undefined &&
            !item.symbol.includes(this.filter.issue)
          ) {
            item.issueID = item.symbol;
            item.issueName = item.symbolName;
            item.issue = `${item.symbol} - ${item.symbolName}`;
          }
        },
      );

      if (this.updatesFromSymbolSelector) {
        this.currentSymbolSubscription = this.store
          .select('currentSymbol')
          .pipe(
            map((value: any) => (value ? value.toJS() : {})),
            tap(
              ({ clearSearchBox }: SearchBoxIssueItem) =>
                clearSearchBox && this.clearBox(),
            ),
            filter(({ issueID }: SearchBoxIssueItem) => issueID !== undefined),
          )
          .subscribe((issue: SearchBoxIssueItem) => {
            this.doUpdatePrice = true;

            if (this.currentIssue) {
              this.unsubscribeSymbol(this.currentIssue.issueID);
              this.unsubscribeSolaceTopics();
            }

            this.currentIssue = issue;
            this.filterText = this.currentIssue.issueID;

            this.subscribeSolace(
              this.currentIssue.issueID,
              this.currentIssue.instrumentType,
            );

            /**
             * TODO (DECOUPLING):
             * This is still set here because it's the only workflow that assigns
             * the property, and the property is still used in unsubscribeSymbol()
             */
            this.currentMarketName = getMarketName(issue.instrumentType);

            this.obtainSymbolFromIssue(issue);
          });
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribeSymbol();
    this.showNoResult = true;
    this.noScroll(!this.showNoResult);
  }

  get showIconClose() {
    return this.filterText.length > 0;
  }

  public changeFilter(text: string): void {
    this.filterText = text.trim();

    if (
      this.filterText === '' ||
      (this.currentIssue && this.filterText.length <= 1)
    ) {
      this.clearBox(text);
    }

    if (!this.filterText.length) {
      return;
    }

    // It may seem this code is unreachable when length is 0 (and it is),
    // but for scenarios that matter, this flag will be updated in clearBox
    this.showItems = !!this.filterText.length;
    this.updateSearchResults();
  }

  protected changeFilterTab(type: number): void {
    this.selectedFilter = type;
    switch (this.selectedFilter) {
      case 0:
        delete this.filter.instrumentType;
        break;
      case 1:
        this.filter.instrumentType = 0;
        break;
      case 2:
        this.filter.instrumentType = 2;
        break;
      default:
        break;
    }
    this.updateSearchResults();
  }

  protected updateSearchResults(): void {
    const searchRegExp = this.buildRegexWithWordBoundary(this.filterText);
    const displayedItemsLimit = 50;
    let displayedItems = 0;

    this.itemsToShow = this.items
      ? this.items.filter(
          (
            item: SearchBoxIssueItem & { symbol: string; symbolName: string },
          ) => {
            // This is an attempt to short-circuit once enough items have been
            // found. An ideal solution would be to use a for-loop + break
            if (displayedItems >= displayedItemsLimit) {
              return;
            }

            if (
              this.filter.instrumentType !== undefined &&
              item.instrumentType !== this.filter.instrumentType
            ) {
              return;
            }

            const shouldDisplay = searchRegExp.test(item.issue || item.symbol);

            if (shouldDisplay) {
              displayedItems++;
            }

            return shouldDisplay;
          },
        )
      : [];

    this.showNoResult =
      this.itemsToShow !== undefined && this.itemsToShow.length === 0;
    this.noScroll(!this.showNoResult);
  }

  /**
   * Creates a RegExp that lets us search only at beginning of words
   * @param query that will be treated as a literal
   * @returns the query as literal, preceded by a word boundary
   */
  private buildRegexWithWordBoundary(query: string) {
    // First we clean all the metacharacters of a regular expression, as to
    // prevent unwanted inputs from creating a non-literal expression,
    // which could lead to ReDoS
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // \b is a word boundary metacharacter:
    // It matches a word boundary position between a word character and
    // non-word character or position (start / end of string).
    // It has no length.
    return new RegExp(`\\b${escapedQuery}`, 'i');
  }

  protected rowSelected(row: SearchBoxIssueItem) {
    this.selectedRow = row;
    this.itemIndexFocused = -1;
    this.closeWhenOutsideClick(true);
    this.noScroll(false);
  }

  protected clearBox(text: string = '') {
    if (!this.disabled) {
      this.filterText = text;

      this.currentRow = undefined;
      this.showItems = false;
      this.itemIndexFocused = -1;

      this.showExtraData = false;
      this.showNoResult = false;
      this.priceDetail = {};
      this.unsubscribeSymbol();
      this.unsubscribeSolaceTopics();

      this.filter.issue = '';

      if (this.updatesFromSymbolSelector) {
        this.store.dispatch({
          type: OPERATION_HARD_RESET,
          payload: { hardReset: true },
        });

        this.store.dispatch({
          type: OPERATION_HARD_RESET,
          payload: { clearText: true },
        });

        this.store.dispatch({
          type: CURRENT_SYMBOL,
          payload: {},
        });
      }

      this.focusOnInput();
      this.noScroll(false);
      this.clearSelectedRow.next(text);
    }
  }

  protected focusOnInput() {
    this.searchInputElement.nativeElement.focus();
  }

  protected onItemKeyDown(event: KeyboardEvent, item: SearchBoxIssueItem) {
    const KEY_ENTER = 13;
    if (event && item && event.keyCode === KEY_ENTER) {
      this.rowSelected(item);
    }
  }

  protected getFocusableElements() {
    const focusableElementsString = 'li.option';
    this.focusableElements = this._elementRef.nativeElement.querySelectorAll(
      focusableElementsString,
    );
    this.focusableElements = Array.prototype.slice.call(this.focusableElements);

    this.firstTabStop = this.focusableElements[0];
    this.lastTabStop =
      this.focusableElements[this.focusableElements.length - 1];
  }

  // @HostListener('document:click', ['$event'])
  // protected onHostDocumentClick(event: MouseEvent) {
  //   const componentReference = this._elementRef.nativeElement;
  //   if (
  //     componentReference === undefined ||
  //     event === undefined ||
  //     !componentReference.contains(event.target)
  //   ) {
  //     this.showItems = false;
  //   }
  // }

  @HostListener('document:keydown', ['$event'])
  protected onHostDocumentKeyDown(event: KeyboardEvent) {
    this.getFocusableElements();
    const componentReference = this._elementRef.nativeElement;
    const KEY_UP = 38;
    const KEY_DOWN = 40;

    if (
      componentReference &&
      event &&
      componentReference.contains(event.target) &&
      (event.keyCode === KEY_UP || event.keyCode === KEY_DOWN)
    ) {
      if (this.focusableElements && this.focusableElements.length) {
        if (event.keyCode === KEY_UP && this.itemIndexFocused > 0) {
          this.itemIndexFocused--;
        } else if (event.keyCode === KEY_DOWN) {
          if (this.itemIndexFocused === -1) {
            this.itemIndexFocused = 0;
          } else if (this.itemIndexFocused >= this.itemsToShow.length - 1) {
            this.itemIndexFocused = this.itemsToShow.length - 1;
          } else {
            this.itemIndexFocused++;
          }
        }
        if (this.focusableElements[this.itemIndexFocused]) {
          if (document.activeElement === this.lastTabStop) {
            event.preventDefault();
            this.itemIndexFocused = 0;
            this.firstTabStop.focus();
          } else {
            this.focusableElements[this.itemIndexFocused].focus();
          }
        }
      }
    }
  }

  public onInputFocus(event: FocusEvent) {
    if (event && event.target && event.target instanceof HTMLInputElement) {
      const inputElement = event.target as HTMLInputElement;
      inputElement.select();
    }
  }

  public closeWhenOutsideClick(event: any) {
    if (this.showItems || event === true) {
      this.noScroll(false);
      this.attachedClick = !this.attachedClick;
      this.showItems = !this.showItems;
      this.showNoResult = false;
    }
    if (event === true) {
      this.showItems = false;
    }
  }

  /**
   * Obtains the Symbol data from a given Issue
   * @param issue Issue we'll be getting the Symbol data from
   */
  private obtainSymbolFromIssue(issue: SearchBoxIssueItem) {
    const marketObservable$ =
      this.sharedSolaceStreamService.getObservableSymbol<MarketItem>(
        getMarketName(issue.instrumentType),
        issue.issueID,
        this.consumerId,
      );

    if (this.symbolSubscription) {
      this.symbolSubscription.unsubscribe();
    }

    this.symbolSubscription = marketObservable$.subscribe((symbol) => {
      this.setPriceDetailsFromSymbol(symbol);
    });
  }

  /**
   * Sets the searchbox price details (last price, change) from a MarketItem
   *
   * @param symbol Contains the data that will be set in the input's priceDetail
   */
  private setPriceDetailsFromSymbol(symbol: MarketItem): void {
    this.showExtraData = true;

    if (symbol !== undefined) {
      let buy;
      let sell: number;
      const last = symbol.hechos.body.lastPrice;

      if (
        this.isUsaContract &&
        this.propertyForDisplayedPrice &&
        symbol.posturas &&
        symbol.posturas.body &&
        symbol.posturas.body.length > 0
      ) {
        buy = symbol.posturas.body[0].SellPrice || last;
        sell = symbol.posturas.body[0].BuyPrice || last;
      }

      this.priceDetail = {
        symbol: symbol.hechos.symbol,
        change: symbol.hechos.body.lastPrice - symbol.hechos.closePrice,
        changeAvg: symbol.hechos.body.variationPercentage / 100,
        last,
        buy,
        sell,
      };
      this.setTradeCapitalsPrice();
    }
  }

  private setTradeCapitalsPrice() {
    /**
     * TODO (DECOUPLING):
     * Use a parameter instead of a property to check if the OPERATION_PRICE
     * event should be dispatched. Or check if we even need to handle it in
     * this component.
     */
    if (this.doUpdatePrice) {
      this.store.dispatch({
        type: OPERATION_PRICE,
        payload: { price: this.priceDetail.last },
      });
      this.doUpdatePrice = false;
    }
  }

  private isActiveLevel2(): boolean {
    let hasLevel2 = false;
    /**
     * TODO (CHORE):
     * This never gets unsubscribed. Maybe we don't even need to
     * assign the resulting subscription.
     */
    const currentUserSubscription = this.store
      .select('loggedInUser')
      .pipe(map((values: any) => values.toJS()))
      .subscribe((value: any) => {
        hasLevel2 = value.hasLevel2;
      });

    return hasLevel2;
  }

  private subscribeSolace(issue: string = '', instrumentTypeId: number) {
    let topicsSubscribe = '';
    if (this.isActiveLevel2()) {
      topicsSubscribe =
        instrumentTypeId === 0
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_SIC;
    } else {
      topicsSubscribe =
        instrumentTypeId === 0
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_SIC;
    }
    this.sharedSolaceMainService.subscribeTopic(topicsSubscribe, issue);
  }

  private unsubscribeSolaceTopics() {
    if (this.currentIssue === undefined) {
      return;
    }

    const MDWEB =
      this.currentIssue.instrumentType === MarketType.BMV
        ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV
        : SOLACE_SUBSCRIPTIONS.MONITOR_SIC;
    const AGGREGATE =
      this.currentIssue.instrumentType === MarketType.BMV
        ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV_VOL
        : SOLACE_SUBSCRIPTIONS.MONITOR_SIC_VOL;

    const marketName: string = getMarketName(this.currentIssue.instrumentType);

    this.sharedSolaceStreamService.removeConsumerFromObservable(
      this.currentIssue.issueID,
      marketName,
      this.consumerId,
    );

    const consumerFromObservable$: any =
      this.sharedSolaceStreamService.getConsumerFromObservable(
        this.currentIssue.issueID,
        marketName,
        this.consumerId,
      );
    const consumers = consumerFromObservable$
      ? consumerFromObservable$.length
      : 0;
    if (consumers === 0) {
      // console.warn('hay que hacer el unsuscribe this.currentIssue.issueID: %o', this.currentIssue.issueID);

      this.sharedSolaceMainService.unSubscribeTopic(
        MDWEB,
        this.currentIssue.issueID,
      );

      this.sharedSolaceMainService.unSubscribeTopic(
        AGGREGATE,
        this.currentIssue.issueID,
      );
    }
  }

  private subscribeSolaceTopics(issue: SearchBoxIssueItem) {
    const topicsToSubscribe =
      issue.instrumentType === MarketType.BMV
        ? [
            SOLACE_SUBSCRIPTIONS.MONITOR_BMV,
            SOLACE_SUBSCRIPTIONS.MONITOR_BMV_VOL,
          ]
        : [
            SOLACE_SUBSCRIPTIONS.MONITOR_SIC,
            SOLACE_SUBSCRIPTIONS.MONITOR_SIC_VOL,
          ];

    topicsToSubscribe.forEach((topic) =>
      this.sharedSolaceMainService.subscribeTopic(topic, issue.issueID),
    );
  }

  private unsubscribeSymbol(symbol?: string) {
    if (this.currentIssue === undefined) {
      return;
    }

    symbol = symbol ? symbol : this.currentIssue.issueID;
    // console.warn('unsubscribeSymbol SEARCH-BOX: symbol: %o    this.currentMarketName: %o  this.symbolSubscription: %o', symbol, this.currentMarketName,this.symbolSubscription);
    if (this.symbolSubscription) {
      this.symbolSubscription.unsubscribe();

      if (this.currentMarketName) {
        this.sharedSolaceStreamService.removeConsumerFromObservable(
          symbol,
          this.currentMarketName,
          this.consumerId,
        );
      }
    }

    if (symbol !== undefined && this.currentMarketName) {
      const consumerFromObservable$: any =
        this.sharedSolaceStreamService.getConsumerFromObservable(
          symbol,
          this.currentMarketName,
          this.consumerId,
        );
      const consumers = consumerFromObservable$
        ? consumerFromObservable$.length
        : 0;

      if (consumers === 0) {
        let topicsSubscribe = '';
        if (this.isActiveLevel2()) {
          topicsSubscribe =
            this.currentIssue.instrumentType === MarketType.BMV
              ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_BMV
              : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_SIC;
        } else {
          topicsSubscribe =
            this.currentIssue.instrumentType === MarketType.BMV
              ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_BMV
              : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_SIC;
        }

        this.sharedSolaceMainService.unSubscribeTopic(topicsSubscribe, symbol);
      }
      if (consumers === 1 && consumerFromObservable$[0] === 'monitor') {
        let topicsSubscribe = '';
        if (this.isActiveLevel2()) {
          topicsSubscribe =
            this.currentIssue.instrumentType === MarketType.BMV
              ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_BMV
              : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_SIC;
        } else {
          topicsSubscribe =
            this.currentIssue.instrumentType === MarketType.BMV
              ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_BMV
              : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_SIC;
        }

        this.sharedSolaceMainService.unSubscribeTopic(topicsSubscribe, symbol);
      }
    }
  }

  private noScroll(block: boolean) {
    this.store.dispatch({
      type: NO_SCROLL,
      payload: { block, source: 'SearchBoxComponent' },
    });
  }
}
